import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowRightLeft, IndianRupee, Loader2, Mail, Pencil, Printer, ShieldCheck, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Separator } from "@/components/ui/separator";
import { DOC_CONFIG } from "./config";
import { EmailDialog } from "./EmailDialog";
import { PaymentDialog } from "./PaymentDialog";

export const DocumentView = ({ kind, docId, open, onOpenChange, onChanged, onEdit }) => {
  const cfg = DOC_CONFIG[kind];
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const load = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/${kind}/${docId}`);
      setData(data);
    } catch (e) { toast.error(errMsg(e)); onOpenChange(false); } finally { setLoading(false); }
  }, [docId, kind, onOpenChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const doc = data?.doc;
  const company = data?.company || {};
  const bank = data?.bank || {};

  const setStatus = async (status) => {
    try {
      await api.post(`/documents/${kind}/${docId}/status`, { status });
      toast.success(`Status updated to ${status}`);
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const convert = async () => {
    try {
      const { data: nd } = await api.post(`/documents/${kind}/${docId}/convert`);
      toast.success(`Created ${nd.number}`);
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto scroll-slim p-0 sm:max-w-3xl">
        <SheetTitle className="sr-only">{doc?.number || cfg.singular}</SheetTitle>
        <SheetDescription className="sr-only">
          {cfg.singular} details, status, payments and email actions.
        </SheetDescription>
        {loading || !doc ? (
          <div className="grid h-full place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="flex min-h-full flex-col">
            {/* Action bar */}
            <div className="sticky top-0 z-40 flex flex-wrap items-center gap-2 border-b border-border bg-background/95 px-5 py-3 backdrop-blur print:hidden">
              <div className="mr-auto">
                <p className="num font-display text-lg font-bold">{doc.number}</p>
                <p className="text-xs text-muted-foreground">{cfg.singular} . {fdate(doc.date)}</p>
              </div>
              <Select value={doc.status} onValueChange={setStatus}>
                <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="doc-status"><SelectValue /></SelectTrigger>
                <SelectContent>{cfg.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {can(cfg.module, "edit") && (
                <Button size="sm" variant="outline" onClick={() => onEdit?.(doc)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
              )}
              {can(cfg.module, "email") && (
                <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} data-testid="doc-email-btn">
                  <Mail className="h-3.5 w-3.5" /> Email
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="h-3.5 w-3.5" /> Print / PDF</Button>
              {cfg.convertTo && (
                <Button size="sm" variant="soft" onClick={convert} data-testid="doc-convert">
                  <ArrowRightLeft className="h-3.5 w-3.5" /> Convert to {cfg.convertTo}
                </Button>
              )}
              {cfg.hasPayments && can(cfg.module, "edit") && (
                <Button size="sm" variant="success" onClick={() => setPayOpen(true)} data-testid="doc-payment">
                  <IndianRupee className="h-3.5 w-3.5" /> Record payment
                </Button>
              )}
              <SheetClose asChild>
                <Button size="sm" variant="ghost" data-testid="doc-close" title="Close">
                  <X className="h-4 w-4" /> Close
                </Button>
              </SheetClose>
            </div>

            {/* Printable document */}
            <div id="print-area" className="flex-1 bg-card px-6 py-7 sm:px-10">
              <div className="flex flex-wrap items-start justify-between gap-6 border-b border-border pb-6">
                <div className="flex items-start gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-lg bg-gradient-warm text-primary-foreground">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-display text-lg font-bold">{company.name}</p>
                    <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">{company.address}</p>
                    <p className="text-xs text-muted-foreground">{company.phone} . {company.email}</p>
                    {company.gstin && <p className="num text-xs text-muted-foreground">GSTIN: {company.gstin}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl font-bold uppercase tracking-wide text-primary">{cfg.singular}</p>
                  <p className="num text-sm font-semibold">{doc.number}</p>
                  <p className="text-xs text-muted-foreground">Date: {fdate(doc.date)}</p>
                  {doc.valid_until && <p className="text-xs text-muted-foreground">Valid until: {fdate(doc.valid_until)}</p>}
                  {doc.due_date && <p className="text-xs text-muted-foreground">Due: {fdate(doc.due_date)}</p>}
                  {doc.expected_delivery && <p className="text-xs text-muted-foreground">Delivery: {fdate(doc.expected_delivery)}</p>}
                  <div className="mt-2 flex justify-end"><StatusBadge status={doc.status} /></div>
                </div>
              </div>

              <div className="grid gap-6 py-6 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {cfg.party === "supplier" ? "Supplier" : "Bill to"}
                  </p>
                  <p className="mt-1 text-sm font-semibold">{doc.supplier_name || doc.customer_name}</p>
                  {doc.company && <p className="text-sm">{doc.company}</p>}
                  {doc.address && <p className="text-xs leading-relaxed text-muted-foreground">{doc.address}</p>}
                  {doc.phone && <p className="text-xs text-muted-foreground">{doc.phone}</p>}
                  {doc.email && <p className="text-xs text-muted-foreground">{doc.email}</p>}
                  {doc.gstin && <p className="num text-xs text-muted-foreground">GSTIN: {doc.gstin}</p>}
                </div>
                <div className="sm:text-right">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Prepared by</p>
                  <p className="mt-1 text-sm font-semibold">{doc.created_by_name}</p>
                  <p className="text-xs text-muted-foreground">{fdatetime(doc.created_at)}</p>
                  {doc.purpose && <p className="mt-2 text-xs text-muted-foreground">Purpose: {doc.purpose}</p>}
                  {doc.payment_terms && <p className="text-xs text-muted-foreground">Terms: {doc.payment_terms}</p>}
                  {doc.source_doc && <p className="num text-xs text-primary">Converted from {doc.source_doc}</p>}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="border-y border-border bg-muted/50">
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
                      <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Qty</th>
                      <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rate</th>
                      <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Tax</th>
                      <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(doc.items || []).map((it, i) => (
                      <tr key={i} className="border-b border-border/70">
                        <td className="px-3 py-3">
                          <p className="font-medium">{it.name}</p>
                          {it.description && <p className="text-xs text-muted-foreground">{it.description}</p>}
                        </td>
                        <td className="num px-3 py-3 text-right">{it.qty}</td>
                        <td className="num px-3 py-3 text-right">{money(it.price)}</td>
                        <td className="num px-3 py-3 text-right">{it.tax_rate}%</td>
                        <td className="num px-3 py-3 text-right font-semibold">{money(it.qty * it.price)}</td>
                      </tr>
                    ))}
                    {(doc.charges || []).filter((c) => c.show_on_print !== false).map((c, i) => (
                      <tr key={`c${i}`} className="border-b border-border/70" data-testid={`view-charge-${i}`}>
                        <td className="px-3 py-3">
                          <p className="font-medium">{c.name}</p>
                          {c.kind === "percent" && (
                            <p className="text-xs text-muted-foreground">{c.value}% of subtotal</p>
                          )}
                        </td>
                        <td className="num px-3 py-3 text-right">1</td>
                        <td className="num px-3 py-3 text-right">
                          {money(c.kind === "percent" ? (doc.subtotal * c.value) / 100 : c.value)}
                        </td>
                        <td className="num px-3 py-3 text-right">{c.taxable === false ? "\u2014" : `${c.tax_rate}%`}</td>
                        <td className="num px-3 py-3 text-right font-semibold">
                          {money(c.kind === "percent" ? (doc.subtotal * c.value) / 100 : c.value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-5 flex justify-end">
                <div className="w-full max-w-xs space-y-1.5">
                  <Line label="Subtotal" value={money(doc.subtotal)} testId="view-subtotal" />
                  {Number(doc.charges_total) > 0 && (
                    <Line label="Additional charges" value={money(doc.charges_total)} testId="view-charges" />
                  )}
                  {Number(doc.discount) > 0 && (
                    <Line label="Discount" value={`- ${money(doc.discount)}`} tone="text-destructive" testId="view-discount" />
                  )}
                  <Line label="Tax" value={money(doc.tax_total)} testId="view-tax" />
                  <div className="flex items-center justify-between border-t border-border pt-2">
                    <span className="text-sm font-semibold">Grand total</span>
                    <span className="num text-lg font-bold text-primary" data-testid="view-grand">{money(doc.total)}</span>
                  </div>
                  {cfg.hasPayments && Number(doc.paid_amount) > 0 && (
                    <>
                      <Line label="Received" value={money(doc.paid_amount)} tone="text-success" />
                      <Line label="Outstanding" value={money(Math.max(0, doc.total - doc.paid_amount))} tone="text-warning" />
                    </>
                  )}
                </div>
              </div>

              {doc.notes && (
                <div className="mt-6">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
                  <p className="mt-1 whitespace-pre-line text-sm leading-relaxed">{doc.notes}</p>
                </div>
              )}
              {doc.terms && (
                <div className="mt-5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Terms and conditions</p>
                  <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">{doc.terms}</p>
                </div>
              )}
              {(kind === "proformas" || kind === "invoices") && bank.bank_name && (
                <div className="mt-5 rounded-lg border border-border bg-muted/40 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Bank details</p>
                  <div className="mt-1 grid gap-x-6 text-xs sm:grid-cols-2">
                    <p>{bank.account_name}</p>
                    <p className="num">A/C {bank.account_number}</p>
                    <p>{bank.bank_name}, {bank.branch}</p>
                    <p className="num">IFSC {bank.ifsc}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Payment history */}
            {cfg.hasPayments && (data?.payments || []).length > 0 && (
              <div className="border-t border-border px-6 py-5 print:hidden sm:px-10">
                <p className="font-display text-sm font-semibold">Payment history</p>
                <Separator className="my-3" />
                <div className="space-y-2">
                  {data.payments.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-4 py-2.5">
                      <div>
                        <p className="num text-sm font-semibold">{money(p.amount)} <span className="text-xs font-normal text-muted-foreground">via {p.mode}</span></p>
                        <p className="text-xs text-muted-foreground">{fdate(p.date)} . {p.reference || "No reference"} . Received by {p.received_by}</p>
                      </div>
                      <StatusBadge status={p.number} tone="neutral" dot={false} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {doc && <EmailDialog kind={kind} doc={doc} open={emailOpen} onOpenChange={setEmailOpen} onSent={() => { load(); onChanged?.(); }} />}
        {doc && <PaymentDialog doc={doc} open={payOpen} onOpenChange={setPayOpen} onSaved={() => { load(); onChanged?.(); }} />}
      </SheetContent>
    </Sheet>
  );
};

const Line = ({ label, value, tone = "", testId }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`num font-medium ${tone}`} data-testid={testId}>{value}</span>
  </div>
);
